import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid, TextField,
    Typography
} from "@mui/material";
import {
    ErrorResponse,
    Loading,
    MatesDatePicker,
    MatesSelect,
    MatesTextField,
    RestResponse,
    withUseFormHook
} from "@sinossi/mates-react-library";
import * as Yup from "yup";
import { UseFormReturn } from "react-hook-form";
import { t, Trans } from "@lingui/macro";
import {
    Asset,
    AssetFilter,
    Brand,
    BrandManagementRequest,
    ContractDetailItem, CreateContractWithId,
    PaginatedResponse,
    WorkflowObjectType
} from "../../models";
import { MatesComponent, MatesComponentState } from "../mates-component";
import { UseRouterParams } from "../../services/router";
import { AssetService } from "../../services/asset.service";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiTimelineText } from "@mdi/js";
import { BrandManagementServices } from "../../services/brand-management.services";
import { ContractServices } from "../../services/contract.services";
import { DateTime } from "luxon";
import { WorkflowService } from "../../services/workflow.service";
import { Autocomplete } from "@mui/lab";

interface CreateContractModalProps extends UseFormReturn, UseRouterParams {
    onClose: () => void
    submit: (request: CreateContractWithId, templateId?: string) => void
    contractId: string
}

interface CreateContractModalState extends MatesComponentState {
    assets: Asset[]
    brands: Brand[]
    templates: { id: string, name: string }[]
    contractData: ContractDetailItem | null
    loading: boolean
    minEndDate: DateTime | undefined
    maxStartDate: DateTime | undefined
    minStartDate: DateTime | undefined
    maxSignDate: DateTime | undefined
    createWorkflow: boolean
    backButton: boolean
    brandValue: any
    brandInputValue: string
    brandOptions: OptionsType[]
    assetValue: any
    assetInputValue: string
    assetOptions: OptionsType[]
    timeoutBrand: any
    timeoutAsset: any
}

interface OptionsType {
    id: string,
    name: string
}

const CreateContractValidation = Yup.object().shape({
    contractNumber: Yup.string().required("Il campo è obbligatorio"),
    contractState: Yup.string().required("Il campo è obbligatorio"),
    brand: Yup.string().required("Il campo è obbligatorio"),
    asset: Yup.string().required("Il campo è obbligatorio"),
    templateId: Yup.string().when("createWorkflow", {
        is: true,
        then: (schema: any) => Yup.string().required("Il campo è obbligatorio")
    })
})


class CreateContractModal extends React.Component<CreateContractModalProps, CreateContractModalState> {

    private readonly matesComponent: MatesComponent<CreateContractModalProps, CreateContractModalState> = new MatesComponent(this)

    constructor(props: CreateContractModalProps) {
        super(props);
        this.state = {
            ...this.matesComponent.setupState(),
            assets: [],
            brands: [],
            templates: [],
            contractData: null,
            loading: true,
            minEndDate: undefined,
            maxStartDate: undefined,
            minStartDate: undefined,
            maxSignDate: undefined,
            createWorkflow: false,
            backButton: false,
            brandValue: null,
            brandInputValue: "",
            brandOptions: [],
            assetValue: null,
            assetInputValue: "",
            assetOptions: [],
            timeoutBrand: null,
            timeoutAsset: null
        }
    }

    componentDidMount() {
        if (this.props.contractId !== "") {
            this.getContract()
        }

        this.getTemplates()
        this.props.watch((value, { name }) => this.handleDateChanges(value))
    }

    private getTemplates = () => {
        WorkflowService.getTemplateListByType(WorkflowObjectType.CONTRACT, this.getTemplatesCallback, this.matesComponent.onError)
    }

    private getTemplatesCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (response.data) {
            this.setState(() => ({
                templates: response.data,
                loading: false
            }))
        }
    }

    private getContract = () => {
        ContractServices.getContract(this.props.contractId, this.getContractCallback, this.matesComponent.onError)
    }

    private getContractCallback = (response: RestResponse<ContractDetailItem, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (response.data) {
            this.setState(() => ({
                contractData: response.data,
            }), this.setFormValues)
        }
    }

    private submit = (values: any) => {
        if (this.state.brandValue.id && this.state.assetValue.id) {
            const request: CreateContractWithId = {
                id: this.props.contractId,
                contractNumber: this.props.getValues("contractNumber"),
                status: this.props.getValues("contractState"),
                brandId: this.state.brandValue.id,
                assetId: this.state.assetValue.id,
                startDate: this.props.getValues("startDate"),
                endDate: this.props.getValues("endDate"),
                signDate: this.props.getValues("signDate"),
            }
            const templateId: string = this.props.getValues("templateId")

            if (!this.state.createWorkflow) {
                this.props.submit(request)
            }
            else {
                this.props.submit(request, templateId)
            }
        }
    }

    private handleAddressChange = (value: string) => {

        this.setState(() => ({
            loading: true
        }))

        if (value !== "") {
            let request: AssetFilter = {
                address: value,
                pageSize: 50,
                pageId: 0,
                orderBy: null,
                orderType: null
            }
            AssetService.filterAssets(request, this.handleAddressChangeCallback, this.matesComponent.onError)
        }
    }

    private handleAddressChangeCallback = (response: RestResponse<PaginatedResponse<Asset>, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (response.data) {
            this.setState(() => ({
                assets: response.data!.items,
                loading: false
            }), this.setAddressOptions)
        }
    }

    private handleBrandChange = (value: string) => {

        this.setState(() => ({
            loading: true
        }))

        if (value !== "") {
            let request: BrandManagementRequest = {
                name: value,
                pageSize: 50
            }

            BrandManagementServices.brandManagementData(request, this.handleBrandChangeCallback, this.matesComponent.onError)
        }
    }

    private handleBrandChangeCallback = (response: RestResponse<PaginatedResponse<Brand>, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (response.data) {
            this.setState(() => ({
                brands: response.data!.items,
                loading: false
            }), this.setBrandOptions)
        }
    }

    private handleDateChanges = (value: any) => {
        if (value.startDate) {
            this.setState(() => ({
                minEndDate: value.startDate,
                maxSignDate: value.startDate
            }))
        }
        if (value.endDate) {
            this.setState(() => ({
                maxStartDate: value.endDate,
                maxSignDate: this.state.maxSignDate !== undefined ? value.startDate : this.state.maxSignDate
            }))
        }
        if (value.signDate) {
            this.setState(() => ({
                minStartDate: value.signDate,
                minEndDate: this.state.minEndDate !== undefined ? value.signDate : this.state.maxSignDate
            }))
        }
    }

    private setFormValues = () => {
        let minEndDate: DateTime | undefined = undefined
        let maxStartDate: DateTime | undefined = undefined
        let minStartDate: DateTime | undefined = undefined
        let maxSignDate: DateTime | undefined = undefined
        let brand: OptionsType | null = null
        let asset: OptionsType | null = null

        if (this.state.contractData) {
            this.props.setValue("contractNumber", this.state.contractData.contractNumber)
            this.props.setValue("contractState", this.state.contractData.status)
            if (this.state.contractData.brand) {
                this.props.setValue("brand", this.state.contractData.brand.name)
            }
            if (this.state.contractData.asset) {
                this.props.setValue("asset", this.state.contractData.asset.address)
            }
            if (this.state.contractData.startDate) {
                this.props.setValue("startDate", DateTime.fromISO(this.state.contractData.startDate.toString()))
                minEndDate = DateTime.fromISO(this.state.contractData!.startDate!.toString())
                maxSignDate = DateTime.fromISO(this.state.contractData!.startDate!.toString())
            }
            if (this.state.contractData.endDate) {
                this.props.setValue("endDate", DateTime.fromISO(this.state.contractData.endDate.toString()))
                maxStartDate = DateTime.fromISO(this.state.contractData!.endDate!.toString())
                if (!maxSignDate) {
                    maxSignDate = DateTime.fromISO(this.state.contractData.endDate.toString())
                }
            }
            if (this.state.contractData.signDate) {
                this.props.setValue("signDate", DateTime.fromISO(this.state.contractData.signDate.toString()))
                minStartDate = DateTime.fromISO(this.state.contractData.signDate.toString())
                if (!minEndDate) {
                    minEndDate = DateTime.fromISO(this.state.contractData.signDate.toString())
                }
            }
            if (this.state.contractData.brand) {
                brand = { id: this.state.contractData.brand.id, name: this.state.contractData.brand.name }
            }

            if (this.state.contractData.asset) {
                asset = { id: this.state.contractData.asset.id, name: this.state.contractData.asset.address }
            }

            this.props.setValue("createWorkflow", this.state.createWorkflow)
        }

        this.setState(() => ({
            maxStartDate: maxStartDate,
            minStartDate: minStartDate,
            minEndDate: minEndDate,
            maxSignDate: maxSignDate,
            brandValue: brand,
            assetValue: asset
        }))


    }

    private openCreateWorkflow = () => {
        this.setState(() => ({
            createWorkflow: true,
            backButton: true
        }), () => {
            this.props.setValue("createWorkflow", true)
        })
    }

    private closeCreateWorkflow = () => {
        this.setState(() => ({
            createWorkflow: false,
            backButton: false
        }), () => {
            this.props.setValue("createWorkflow", false)
            this.props.setValue("templateId", "")
        })
    }

    private handleValidation = (data: any) => {
        let objKeys = Object.keys(data)
        if (objKeys.length === 1 && objKeys[0] === "templateId") {
            return
        }
        this.closeCreateWorkflow()
    }

    private fetchBrand = (input: string) => {
        if (this.state.timeoutBrand) {
            clearTimeout(this.state.timeoutBrand)
        }
        this.setState(() => ({
            timeoutBrand: setTimeout(() => this.handleBrandChange(input), 1000)
        }))
    }

    private setBrandOptions = () => {
        let brandOptions: OptionsType[] = []

        this.state.brands.map((brand) => brandOptions.push({ id: brand.id, name: brand.name }))

        this.setState(() => ({
            brandOptions: brandOptions
        }))
    }


    private fetchAsset = (input: string) => {
        if (this.state.timeoutAsset) {
            clearTimeout(this.state.timeoutAsset)
        }
        this.setState(() => ({
            timeoutAsset: setTimeout(() => this.handleAddressChange(input), 1000)
        }))
    }

    private setAddressOptions = () => {
        let addressOptions: OptionsType[] = []

        this.state.assets.map((asset) => addressOptions.push({ id: asset.id, name: asset.address }))

        this.setState(() => ({
            assetOptions: addressOptions
        }))
    }

    render() {
        let { brandValue, brandOptions, assetValue, assetOptions } = this.state

        return (
            <Dialog open={true} onClose={this.props.onClose} fullWidth maxWidth="md">
                <DialogTitle>
                    <Typography variant="h5">
                        {
                            this.props.contractId === "" ?
                                <Trans id="create-contact-modal.create-contract">Aggiungi contratto</Trans>
                                :
                                <Trans id="create-contract-modal.edit-contract">Modifica contratto</Trans>
                        }

                    </Typography>
                </DialogTitle>
                <Loading show={this.state.loading} />
                <form onSubmit={this.props.handleSubmit(this.submit, this.handleValidation)}>
                    <DialogContent>
                        {
                            !this.state.createWorkflow &&
                            <Grid container spacing={3} p={2}>
                                <Grid item xs={12} md={6}>
                                    <MatesTextField variant="outlined"
                                        label={t({
                                            id: "create-contract-modal.contract-number",
                                            message: "Numero contratto",
                                        })}
                                        name="contractNumber"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MatesSelect variant="outlined"
                                        label={t({
                                            id: "create-contract-modal.state",
                                            message: "Stato",
                                        })}
                                        name="contractState"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        required
                                    >
                                        <option key={0} value={"ACTIVE"}>
                                            <Trans id={"crate-contract-modal.active"}>Attivo</Trans>
                                        </option>
                                        <option key={1} value={"NEGOTIATING"}>
                                            <Trans id={"crate-contract-modal.working"}>In lavorazione</Trans>
                                        </option>
                                        <option key={2} value={"CLOSED"}>
                                            <Trans id={"crate-contract-modal.closed"}>Chiuso</Trans>
                                        </option>
                                        <option key={3} value={"REJECTED"}>
                                            <Trans id={"crate-contract-modal.rejected"}>Rifiutato</Trans>
                                        </option>
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        getOptionLabel={(option) => option.name}
                                        filterOptions={(x) => x}
                                        options={brandOptions}
                                        autoComplete
                                        includeInputInList
                                        filterSelectedOptions
                                        value={brandValue}
                                        noOptionsText={t({ id: "create-contract-modal.no-brand", message: "Inizia a digitare per avviare la ricerca" })}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                this.setState(() => ({
                                                    brandValue: newValue, brandOptions: [newValue, ...brandOptions]
                                                }));
                                                this.props.setValue("brand", newValue.name)
                                            }
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            this.setState({ brandInputValue: newInputValue }, () => {
                                                if (this.state.brandInputValue === '') {
                                                    this.setState({ brandOptions: brandValue ? [brandValue] : [] });
                                                }
                                                else {
                                                    this.fetchBrand(this.state.brandInputValue)
                                                }
                                            })
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params}
                                                label={t({ id: "create-contract-modal.select-brand", message: "Brand" })}
                                                fullWidth
                                                required
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        getOptionLabel={(option) => option.name}
                                        filterOptions={(x) => x}
                                        options={assetOptions}
                                        autoComplete
                                        includeInputInList
                                        filterSelectedOptions
                                        value={assetValue}
                                        noOptionsText={t({ id: "create-contract-modal.no-asset", message: "Inizia a digitare per avviare la ricerca" })}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                this.setState(() => ({
                                                    assetValue: newValue, assetOptions: [newValue, ...assetOptions]
                                                }));
                                                this.props.setValue("asset", newValue.name)
                                            }
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            this.setState({ assetInputValue: newInputValue }, () => {
                                                if (this.state.assetInputValue === '') {
                                                    this.setState({ assetOptions: assetValue ? [assetValue] : [] });
                                                }
                                                else {
                                                    this.fetchAsset(this.state.assetInputValue)
                                                }
                                            })
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params}
                                                label={t({ id: "create-contract-modal.select-address", message: "Indirizzo" })}
                                                fullWidth
                                                required
                                            />
                                        )} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MatesDatePicker variant="outlined"
                                        label={t({
                                            id: "create-contract-modal.start-date",
                                            message: "Data inizio",
                                        })}
                                        name="startDate"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        maxDate={this.state.maxStartDate}
                                        minDate={this.state.minStartDate}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MatesDatePicker variant="outlined"
                                        label={t({
                                            id: "create-contract-modal.end-date",
                                            message: "Scadenza",
                                        })}
                                        name="endDate"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        minDate={this.state.minEndDate}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MatesDatePicker variant="outlined"
                                        label={t({
                                            id: "create-contract-modal.sign-date",
                                            message: "Data firma",
                                        })}
                                        name="signDate"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        maxDate={this.state.maxSignDate}
                                    />
                                </Grid>
                            </Grid>
                        }
                        {
                            this.state.createWorkflow ?
                                <>
                                    <Typography fontSize={"1rem"} sx={{ mb: 3 }}>
                                        <Trans id="create-contract-modal.select-template">
                                            Seleziona template di riferimento.
                                        </Trans>
                                    </Typography>
                                    <MatesSelect variant={"outlined"}
                                        label={t({
                                            id: "create-contract-modal.template-id",
                                            message: "Nome template",
                                        })}
                                        name="templateId"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    >
                                        {this.state.templates.map((template) => (
                                            <option value={template.id} key={template.id}>
                                                {template.name}
                                            </option>
                                        ))}
                                    </MatesSelect>
                                </>
                                :
                                null
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.props.onClose}
                            variant={"contained"}
                            color="neutral"
                            sx={{
                                textTransform: "none",
                                mr: 1,
                                width: 100
                            }}
                            disableElevation>
                            <Trans id={"create-contract-modal.close-button"}>
                                Annulla
                            </Trans>
                        </Button>
                        <Button type="submit"
                            variant="contained"
                            sx={{
                                backgroundColor: 'secondary.main',
                                color: 'white',
                                mr: 1,
                                textTransform: "none",
                                width: 100
                            }}
                        >
                            {
                                this.props.contractId === "" ?
                                    <Trans id={"create-contract-modal.create-button"}>Crea</Trans>
                                    :
                                    <Trans id="create-contract-modal.update-button">Aggiorna</Trans>
                            }
                        </Button>
                        {
                            !this.state.createWorkflow && !this.props.contractId &&
                            <Button sx={{ textTransform: "none" }}
                                variant="contained"
                                onClick={this.openCreateWorkflow}
                                disableElevation
                                startIcon={<Icon path={mdiTimelineText} size={1} />}
                            >
                                <Trans id={"create-contract-modal.create-workflow"}>
                                    Crea con Workflow
                                </Trans>
                            </Button>
                        }
                        {
                            this.state.createWorkflow &&
                            <Button sx={{ textTransform: "none" }}
                                onClick={this.closeCreateWorkflow}
                                variant="contained"
                                disableElevation
                                startIcon={<Icon path={mdiArrowLeft} size={1} />}
                            >
                                <Trans id={"create-contract-modal.back"}>
                                    Indietro
                                </Trans>
                            </Button>
                        }
                    </DialogActions>
                </form>
            </Dialog>
        )
    }
}

export default withUseFormHook<{}>(
    CreateContractModal,
    CreateContractValidation,
    {}
)
